/**
 * Build-Your-Own-Beyblade parts (DESIGN: build-your-own). A custom bey is a
 * Frame (archetype — sets type, special, art, base stats) plus a Disc and a
 * Driver that shift stats. Pure data + a pure composer. No Phaser.
 */
import { ROSTER, getBeyDef, type BeyDef, type BeyStats, type BeyType, type SpinDir } from './stats.ts';

export type StatMods = Partial<BeyStats>;

export interface Part {
  id: string;
  name: string;
  blurb: string;
  mods: StatMods;
}

/** Frames are the four archetypes; their roster stats are the base profile. */
export const FRAMES: readonly { id: string; type: BeyType }[] = ROSTER.map((b) => ({
  id: b.id,
  type: b.type,
}));

export const DISCS: readonly Part[] = [
  { id: 'heavy', name: 'Heavy Disc', blurb: '+Weight +Defense, −Speed', mods: { weight: 3, defense: 1, speed: -2 } },
  { id: 'round', name: 'Round Disc', blurb: '+Stamina +Durability', mods: { stamina: 1, durability: 1 } },
  { id: 'light', name: 'Light Disc', blurb: '+Speed, −Weight −Stamina', mods: { speed: 3, weight: -2, stamina: -1 } },
];

export const DRIVERS: readonly Part[] = [
  { id: 'attack', name: 'Attack Driver', blurb: '+Attack, −Stamina', mods: { attack: 2, stamina: -2 } },
  { id: 'defense', name: 'Defense Driver', blurb: '+Defense +Durability, −Speed', mods: { defense: 2, durability: 1, speed: -1 } },
  { id: 'stamina', name: 'Stamina Driver', blurb: '+Stamina, −Attack', mods: { stamina: 3, attack: -2 } },
  { id: 'flat', name: 'Flat Driver', blurb: 'No change — balanced', mods: {} },
];

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const lastWord = (s: string) => s.split(' ').pop() ?? s;

const DRIVER_ADJ: Record<string, string> = {
  attack: 'Striker',
  defense: 'Guardian',
  stamina: 'Endurer',
  flat: 'Custom',
};

function applyMods(base: BeyStats, ...mods: StatMods[]): BeyStats {
  const out: BeyStats = { ...base };
  for (const m of mods) {
    for (const k of Object.keys(m) as (keyof BeyStats)[]) {
      out[k] = clamp(out[k] + (m[k] ?? 0), 1, 10);
    }
  }
  return out;
}

export const getDisc = (id: string): Part => DISCS.find((d) => d.id === id) ?? DISCS[0];
export const getDriver = (id: string): Part => DRIVERS.find((d) => d.id === id) ?? DRIVERS[0];

/**
 * Compose a custom BeyDef from a frame + disc + driver. The result keeps the
 * frame's type, special, spin direction, colors, and art, with stats shifted by
 * the parts (clamped 1–10) and id `custom`.
 */
export function buildCustomBey(
  frameId: string,
  discId: string,
  driverId: string,
  spinDir?: SpinDir,
): BeyDef {
  const frame = getBeyDef(frameId);
  const disc = getDisc(discId);
  const driver = getDriver(driverId);
  const stats = applyMods(frame.stats, disc.mods, driver.mods);
  return {
    id: 'custom',
    artId: frame.id,
    name: `${lastWord(frame.name)} ${DRIVER_ADJ[driver.id] ?? 'Custom'}`,
    type: frame.type,
    spinDir: spinDir ?? frame.spinDir,
    color: frame.color,
    accent: frame.accent,
    blurb: `Custom build — ${disc.name} + ${driver.name}.`,
    stats,
  };
}
